import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle2, XCircle, RotateCcw, Trophy, Target } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const QuizComponent = ({ storyId, questions = [], onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const { toast } = useToast();

  const question = questions[currentQuestion];
  const totalQuestions = questions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  useEffect(() => {
    if (isComplete && questions.length > 0) {
      const correctAnswers = Object.entries(answers).filter(([qId, answer]) => {
        const q = questions.find(question => question.id === parseInt(qId));
        return isAnswerCorrect(q, answer);
      }).length;
      
      setScore(Math.round((correctAnswers / questions.length) * 100));
      if (onComplete) {
        onComplete({
          score: Math.round((correctAnswers / questions.length) * 100),
          answers,
          totalQuestions: questions.length,
          correctAnswers
        });
      }
    }
  }, [isComplete, answers, questions, onComplete]);

  const isAnswerCorrect = (question, answer) => {
    switch (question.type) {
      case 'multiple_choice':
        return answer === question.correct;
      case 'fill_blank':
      case 'translation':
        const correctAnswers = [question.correct, ...(question.alternatives || [])];
        return correctAnswers.some(correct => 
          answer?.toLowerCase().trim() === correct.toLowerCase().trim()
        );
      case 'verb_conjugation':
        return answer?.toLowerCase().trim() === question.correct.toLowerCase().trim();
      default:
        return false;
    }
  };

  const handleAnswer = (answer) => {
    const newAnswers = { ...answers, [question.id]: answer };
    setAnswers(newAnswers);
    setShowResult(true);
  };

  const handleNext = () => {
    setShowResult(false);
    setShowExplanation(false);
    
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setIsComplete(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowExplanation(false);
    setIsComplete(false);
    setScore(0);
  };

  const renderQuestion = () => {
    if (!question) return null;

    const userAnswer = answers[question.id];
    const isCorrect = userAnswer !== undefined ? isAnswerCorrect(question, userAnswer) : null;

    switch (question.type) {
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            <p className="text-lg font-medium text-slate-900 mb-4">
              {question.question}
            </p>
            <div className="grid gap-3">
              {question.options.map((option, index) => (
                <Button
                  key={index}
                  variant={
                    showResult
                      ? index === question.correct
                        ? "default"
                        : userAnswer === index
                        ? "destructive"
                        : "outline"
                      : "outline"
                  }
                  className={`p-4 h-auto text-left justify-start ${
                    showResult && index === question.correct
                      ? "bg-green-500 hover:bg-green-600"
                      : showResult && userAnswer === index && userAnswer !== question.correct
                      ? "bg-red-500 hover:bg-red-600"
                      : ""
                  }`}
                  onClick={() => !showResult && handleAnswer(index)}
                  disabled={showResult}
                >
                  <span className="mr-3 font-bold">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  {option}
                </Button>
              ))}
            </div>
          </div>
        );

      case 'fill_blank':
        return (
          <div className="space-y-4">
            <p className="text-lg font-medium text-slate-900">
              {question.question}
            </p>
            <div className="flex gap-3">
              <Input
                placeholder="Ihre Antwort..."
                value={userAnswer || ''}
                onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                disabled={showResult}
                className={
                  showResult
                    ? isCorrect
                      ? "border-green-500 bg-green-50"
                      : "border-red-500 bg-red-50"
                    : ""
                }
              />
              {!showResult && (
                <Button
                  onClick={() => handleAnswer(userAnswer)}
                  disabled={!userAnswer?.trim()}
                >
                  Prüfen
                </Button>
              )}
            </div>
            {showResult && (
              <div className="text-sm">
                <p className="font-medium">Richtige Antwort: {question.correct}</p>
              </div>
            )}
          </div>
        );

      case 'verb_conjugation':
        return (
          <div className="space-y-4">
            <p className="text-lg font-medium text-slate-900">
              {question.question}
            </p>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">
                Verb: <span className="font-bold">{question.verb}</span> | 
                Person: <span className="font-bold">{question.person}</span> | 
                Zeit: <span className="font-bold">{question.tense}</span>
              </p>
            </div>
            <div className="flex gap-3">
              <Input
                placeholder="Konjugierte Form..."
                value={userAnswer || ''}
                onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                disabled={showResult}
                className={
                  showResult
                    ? isCorrect
                      ? "border-green-500 bg-green-50"
                      : "border-red-500 bg-red-50"
                    : ""
                }
              />
              {!showResult && (
                <Button
                  onClick={() => handleAnswer(userAnswer)}
                  disabled={!userAnswer?.trim()}
                >
                  Prüfen
                </Button>
              )}
            </div>
            {showResult && (
              <div className="text-sm">
                <p className="font-medium">Richtige Antwort: {question.correct}</p>
              </div>
            )}
          </div>
        );

      case 'translation':
        return (
          <div className="space-y-4">
            <p className="text-lg font-medium text-slate-900">
              {question.question}
            </p>
            <textarea
              className="w-full p-3 border rounded-lg resize-none"
              rows={3}
              placeholder="Ihre Übersetzung..."
              value={userAnswer || ''}
              onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
              disabled={showResult}
            />
            {!showResult && (
              <Button
                onClick={() => handleAnswer(userAnswer)}
                disabled={!userAnswer?.trim()}
              >
                Prüfen
              </Button>
            )}
            {showResult && (
              <div className="text-sm space-y-2">
                <p className="font-medium">Mögliche Antworten:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>{question.correct}</li>
                  {question.alternatives?.map((alt, i) => (
                    <li key={i}>{alt}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (questions.length === 0) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          <Target className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            Keine Quiz-Fragen verfügbar
          </h3>
          <p className="text-slate-600">
            Für diese Geschichte wurden noch keine Quiz-Fragen erstellt.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isComplete) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-[#4ECDC4] rounded-full flex items-center justify-center mx-auto mb-6">
            <Trophy className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            Quiz abgeschlossen!
          </h3>
          <div className="text-4xl font-bold text-[#4ECDC4] mb-2">
            {score}%
          </div>
          <p className="text-slate-600 mb-6">
            {Object.entries(answers).filter(([qId, answer]) => {
              const q = questions.find(question => question.id === parseInt(qId));
              return isAnswerCorrect(q, answer);
            }).length} von {questions.length} Fragen richtig beantwortet
          </p>
          <Button
            onClick={handleRestart}
            className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Quiz wiederholen
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">
            Quiz - Frage {currentQuestion + 1} von {totalQuestions}
          </CardTitle>
          <Badge variant="outline" className="text-[#4ECDC4] border-[#4ECDC4]">
            {question?.type?.replace('_', ' ')}
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />
      </CardHeader>

      <CardContent className="space-y-6">
        {renderQuestion()}

        {showResult && (
          <Alert className={isAnswerCorrect(question, answers[question.id]) ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
            <div className="flex items-center space-x-2">
              {isAnswerCorrect(question, answers[question.id]) ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className="font-medium">
                {isAnswerCorrect(question, answers[question.id]) ? "Richtig!" : "Falsch!"}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {showResult && question.explanation && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Erklärung:</h4>
            <p className="text-blue-800 text-sm">{question.explanation}</p>
          </div>
        )}

        {showResult && (
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setShowExplanation(!showExplanation)}
            >
              {showExplanation ? "Erklärung ausblenden" : "Erklärung anzeigen"}
            </Button>
            <Button
              onClick={handleNext}
              className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
            >
              {currentQuestion < totalQuestions - 1 ? "Nächste Frage" : "Quiz beenden"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuizComponent;